import React, { useContext, useState, useEffect } from 'react';
import { AuthContext } from '@/App';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import PremiumGate from '@/components/PremiumGate';
import UpgradePrompt from '@/components/UpgradePrompt';
import {
  Sparkles,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  TrendingUp,
  Lightbulb,
  Target,
  AlertCircle,
  Clock,
  Calendar,
  Eye,
  Trash2
} from 'lucide-react';
import { showToast } from '@/lib/custom-toast';

import BACKEND_URL from '@/config/backendUrl';


const AIInsightsImproved = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();

  const [latestInsight, setLatestInsight] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [showFullAnalysis, setShowFullAnalysis] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (token) {
      fetchLatestInsight();
      fetchHistory();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchLatestInsight = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/ai/insights/latest`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok && data.success && data.insight) {
        setLatestInsight(data.insight);
      } else {
        setLatestInsight(null);
      }
    } catch (error) {
      console.error('Error fetching latest insight:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await fetch(`${BACKEND_URL}/api/ai/insights/history?limit=5`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setHistory(data.insights || []);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const generateNewAnalysis = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${BACKEND_URL}/api/ai/insights/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          analysis_type: 'financial_overview',
          date_range_days: 30
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        showToast.success('✨ Análisis generado exitosamente');
        setLatestInsight(data.insight);
        fetchHistory(); // Refresh history
        setShowFullAnalysis(false);
      } else {
        if (response.status === 403) {
          showToast.error('Esta funcionalidad requiere suscripción Premium');
          navigate('/pricing');
        } else {
          showToast.error(data.detail || 'Error al generar análisis');
        }
      }
    } catch (error) {
      console.error('Error generating analysis:', error);
      showToast.error('Error de conexión al generar análisis');
    } finally {
      setLoading(false);
    }
  };

  const deleteInsight = async (insightId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/ai/insights/${insightId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        showToast.success('Análisis eliminado');
        fetchHistory();
        if (latestInsight && latestInsight.id === insightId) {
          setLatestInsight(null);
          fetchLatestInsight();
        }
      } else {
        showToast.error('Error al eliminar análisis');
      }
    } catch (error) {
      console.error('Error deleting insight:', error);
      showToast.error('Error de conexión');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Layout>
      <PremiumGate fallback={
        <UpgradePrompt
          title="Análisis IA Premium"
          message="Desbloquea insights inteligentes sobre tus gastos con análisis impulsado por IA"
        />
      }>
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:to-slate-800 p-4 sm:p-6 lg:p-8">
          <div className="max-w-5xl mx-auto space-y-6">
            {/* Header */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-purple-500 via-blue-500 to-indigo-600 rounded-3xl mb-4 shadow-xl">
                <Sparkles className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-2 sm:mb-3">
                Análisis Financiero IA
              </h1>
              <p className="text-base sm:text-lg text-slate-600 dark:text-slate-400">
                Insights inteligentes sobre tus finanzas personales
              </p>
            </div>

            {/* Latest Insight Card */}
            {latestInsight ? (
              <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-600 px-4 sm:px-6 py-4 sm:py-5">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <h2 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                        <Sparkles className="w-5 h-5 sm:w-6 sm:h-6" />
                        Resumen Ejecutivo
                      </h2>
                      <p className="text-xs sm:text-sm text-white/80 mt-1">
                        <Clock className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />
                        Última actualización: {formatDate(latestInsight.created_at)}
                      </p>
                    </div>
                    <button
                      onClick={generateNewAnalysis}
                      disabled={loading}
                      className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors disabled:opacity-50 text-sm sm:text-base"
                    >
                      <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                      Generar Nuevo
                    </button>
                  </div>
                </div>

                {/* Summary Section */}
                <div className="p-4 sm:p-6">
                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex-shrink-0">
                        <Target className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-base sm:text-lg text-slate-900 dark:text-white mb-2 sm:mb-3">
                          🎯 Puntos Clave
                        </h3>
                        <ul className="space-y-2">
                          {latestInsight.summary.map((point, index) => (
                            <li key={index} className="flex items-start gap-2 text-sm sm:text-base text-slate-700 dark:text-slate-300">
                              <span className="text-purple-600 dark:text-purple-400 font-bold flex-shrink-0">•</span>
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Recommendations */}
                    {latestInsight.recommendations && latestInsight.recommendations.length > 0 && (
                      <div className="flex items-start gap-3 mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex-shrink-0">
                          <Lightbulb className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-base sm:text-lg text-slate-900 dark:text-white mb-2 sm:mb-3">
                            💡 Recomendaciones
                          </h3>
                          <ul className="space-y-2">
                            {latestInsight.recommendations.map((rec, index) => (
                              <li key={index} className="flex items-start gap-2 text-sm sm:text-base text-slate-700 dark:text-slate-300">
                                <span className="text-blue-600 dark:text-blue-400 font-bold flex-shrink-0">→</span>
                                <span>{rec}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Toggle Full Analysis */}
                  <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-slate-200 dark:border-slate-700">
                    <button
                      onClick={() => setShowFullAnalysis(!showFullAnalysis)}
                      className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                    >
                      <span className="font-medium text-sm sm:text-base text-slate-900 dark:text-white">
                        {showFullAnalysis ? 'Ocultar' : 'Ver'} Análisis Completo
                      </span>
                      {showFullAnalysis ? (
                        <ChevronUp className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                      )}
                    </button>

                    {/* Full Analysis Content */}
                    {showFullAnalysis && (
                      <div className="mt-4 p-4 sm:p-5 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 rounded-xl">
                        <h3 className="font-semibold text-base sm:text-lg text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                          <TrendingUp className="w-5 h-5" />
                          Análisis Detallado
                        </h3>
                        <div className="prose prose-sm sm:prose dark:prose-invert max-w-none">
                          <p className="text-sm sm:text-base text-slate-700 dark:text-slate-300 whitespace-pre-line leading-relaxed">
                            {latestInsight.full_analysis}
                          </p>
                        </div>

                        {/* Metadata */}
                        {latestInsight.metadata && (
                          <div className="mt-4 pt-4 border-t border-slate-300 dark:border-slate-600">
                            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                              📊 Transacciones analizadas: {latestInsight.metadata.transactions_count || 0} |
                              📅 Período: últimos {latestInsight.metadata.date_range_days || 30} días
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              // No Analysis Yet
              <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8 sm:p-12 text-center">
                <Sparkles className="w-16 h-16 sm:w-20 sm:h-20 mx-auto text-purple-600 dark:text-purple-400 mb-4" />
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white mb-3">
                  No hay análisis aún
                </h2>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-6 max-w-md mx-auto">
                  Genera tu primer análisis financiero con inteligencia artificial y obtén insights personalizados
                </p>
                <button
                  onClick={generateNewAnalysis}
                  disabled={loading}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all font-medium disabled:opacity-50 shadow-lg"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Generando...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Generar Análisis
                    </>
                  )}
                </button>
              </div>
            )}

            {/* History Section */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl overflow-hidden">
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="w-full flex items-center justify-between px-4 sm:px-6 py-4 sm:py-5 bg-gradient-to-r from-slate-700 to-slate-900 hover:from-slate-800 hover:to-slate-950 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  <h2 className="text-lg sm:text-xl font-bold text-white">
                    Historial de Análisis
                  </h2>
                  {history.length > 0 && (
                    <span className="px-2 py-1 bg-white/20 rounded-full text-xs sm:text-sm text-white font-medium">
                      {history.length}
                    </span>
                  )}
                </div>
                {showHistory ? (
                  <ChevronUp className="w-5 h-5 text-white" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-white" />
                )}
              </button>

              {showHistory && (
                <div className="p-4 sm:p-6">
                  {loadingHistory ? (
                    <div className="text-center py-8">
                      <div className="w-10 h-10 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto" />
                      <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">Cargando historial...</p>
                    </div>
                  ) : history.length === 0 ? (
                    <div className="text-center py-8">
                      <Calendar className="w-12 h-12 mx-auto text-slate-400 mb-3" />
                      <p className="text-slate-600 dark:text-slate-400">No hay análisis previos</p>
                    </div>
                  ) : (
                    <div className="space-y-3 sm:space-y-4">
                      {history.map((insight) => (
                        <div
                          key={insight.id}
                          className="border border-slate-200 dark:border-slate-700 rounded-xl p-4 sm:p-5 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-start justify-between gap-3 mb-3">
                            <div className="flex-1 min-w-0">
                              <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mb-2">
                                {formatDate(insight.created_at)}
                              </p>
                              <ul className="space-y-1">
                                {insight.summary.slice(0, 2).map((point, index) => (
                                  <li key={index} className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 truncate">
                                    • {point}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                              <button
                                onClick={() => {
                                  setLatestInsight(insight);
                                  setShowFullAnalysis(true);
                                  window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                                className="p-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                title="Ver análisis"
                              >
                                <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400" />
                              </button>
                              <button
                                onClick={() => {
                                  if (window.confirm('¿Eliminar este análisis?')) {
                                    deleteInsight(insight.id);
                                  }
                                }}
                                className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                title="Eliminar"
                              >
                                <Trash2 className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 dark:text-red-400" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Info Card */}
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-4 sm:p-5">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-sm sm:text-base text-purple-900 dark:text-purple-300 mb-1">
                    ¿Cómo funciona?
                  </h3>
                  <p className="text-xs sm:text-sm text-purple-800 dark:text-purple-200">
                    Nuestro sistema de IA analiza tus transacciones, patrones de gasto y comportamiento financiero para
                    brindarte recomendaciones personalizadas. Genera un nuevo análisis cada vez que quieras obtener
                    insights actualizados sobre tus finanzas.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PremiumGate>
    </Layout>
  );
};

export default AIInsightsImproved;
